import type { PlanningCase, PlanningDetailResponse } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { api } from '../api/endpoints';
import { permitDetailHref } from '../features/permits/permitDetailHref';

const PERMIT_TYPE_LABELS: Record<string, string> = {
  BP: 'Building permit',
  EP: 'Electrical permit',
  MP: 'Mechanical permit',
  PP: 'Plumbing permit',
};

const permitTypeLabel = (value: string | null) =>
  (value && PERMIT_TYPE_LABELS[value.toUpperCase()]) || value || 'Permit';

const authorityLabel = (value: string | null) =>
  value === 'us-tx-tdlr' ? 'Texas Department of Licensing and Regulation' : value;

function CaseSummary({ row, onOpen }: { row: PlanningCase; onOpen: () => void }) {
  return (
    <ListItem divider secondaryAction={<Button onClick={onOpen}>Inspect</Button>}>
      <ListItemText
        primary={`${row.source_case_number} — ${row.title ?? row.case_type ?? row.case_family ?? 'Planning case'}`}
        secondary={`${row.jurisdiction_name ?? row.state ?? 'Unknown jurisdiction'} · ${row.address_line1 ?? 'relational/no direct address'} · ${row.status_normalized ?? row.status ?? 'unknown status'}`}
      />
    </ListItem>
  );
}

function Detail({ data }: { data: PlanningDetailResponse }) {
  const c = data.case;
  const relatedPermits = data.permits.filter((p) => p.valid_on_case_window !== false);
  const outsideWindowPermits = data.permits.length - relatedPermits.length;
  const contractorCandidates = data.contractors.filter((x) => x.valid_on_activity_date !== false);
  const outsideWindowContractors = data.contractors.length - contractorCandidates.length;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">
        {c.source_case_number} — {c.title ?? c.case_type ?? 'Planning case'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, my: 1, flexWrap: 'wrap' }}>
        <Chip label={c.case_family ?? 'unclassified'} />
        <Chip label={c.status_normalized ?? c.status ?? 'unknown status'} />
        <Chip label={c.jurisdiction_name ?? c.state ?? 'unknown jurisdiction'} />
      </Box>
      <Typography>{c.description ?? 'No description published.'}</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Requested use/action:{' '}
        {c.proposed_use ??
          c.proposed_zoning ??
          (data.actions
            .map((a) => a.action_text)
            .filter(Boolean)
            .join('; ') ||
            '—')}
      </Typography>
      <Typography variant="body2">
        Zoning: {c.existing_zoning ?? '—'} → {c.proposed_zoning ?? '—'} · Applicant:{' '}
        {c.applicant_name ?? '—'} · Decision body: {c.decision_body ?? '—'}
      </Typography>
      <Typography variant="body2">
        Submitted {c.submitted_date ?? '—'} · decided {c.decision_date ?? '—'} · closed{' '}
        {c.closed_date ?? '—'}
      </Typography>
      <Typography variant="body2">
        Direct location:{' '}
        {c.address_line1
          ? `${c.address_line1}, ${c.city ?? ''} ${c.state ?? ''}`
          : 'none published'}{' '}
        {c.latitude != null ? `(${c.latitude}, ${c.longitude})` : ''}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1">
        Parcels / relational locations ({data.parcels.length})
      </Typography>
      <List dense>
        {data.parcels.map((p) => (
          <ListItem key={p.case_parcel_id}>
            <ListItemText
              primary={p.address_line1 ?? p.bbl ?? p.parcel_number ?? 'Unnamed parcel'}
              secondary={`APN ${p.parcel_number ?? '—'} · BBL ${p.bbl ?? '—'}`}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">Actions ({data.actions.length})</Typography>
      <List dense>
        {data.actions.map((a) => (
          <ListItem key={a.action_id}>
            <ListItemText
              primary={a.action_text ?? a.action_type ?? 'Action'}
              secondary={a.disposition ?? undefined}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">Chronological events ({data.events.length})</Typography>
      <List dense>
        {data.events.map((e) => (
          <ListItem key={e.event_id}>
            <ListItemText
              primary={`${e.event_date ?? 'Undated'} — ${e.event_type ?? 'Event'}`}
              secondary={[e.body, e.outcome].filter(Boolean).join(' · ')}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">
        Possible related permits ({relatedPermits.length})
      </Typography>
      {outsideWindowPermits > 0 && (
        <Alert severity="info" sx={{ my: 1 }}>
          {outsideWindowPermits} same-address permit{outsideWindowPermits === 1 ? ' was' : 's were'}
          {' hidden because the permit dates fall outside this planning case activity window.'}
        </Alert>
      )}
      <List dense>
        {relatedPermits.map((p) => (
          <ListItem
            key={p.link_id}
            secondaryAction={
              <Button href={permitDetailHref(p.permit_source_id, p.permit_id)}>Permit</Button>
            }
          >
            <ListItemText
              primary={`${p.source_permit_number ?? p.permit_id} — ${permitTypeLabel(p.permit_type)}`}
              secondary={`${p.permit_address ?? 'No address published'} · matched because it uses the same address${p.candidate_count > 1 ? `; one of ${p.candidate_count} permits at this address` : ''}${p.valid_on_case_window ? ' · permit date overlaps the case activity window' : ' · permit date unavailable'}`}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">
        Possible contractors ({contractorCandidates.length})
      </Typography>
      {outsideWindowContractors > 0 && (
        <Alert severity="info" sx={{ my: 1 }}>
          {outsideWindowContractors} contractor candidate
          {outsideWindowContractors === 1 ? ' was' : 's were'}
          {' hidden because the credential was not valid during the case activity window.'}
        </Alert>
      )}
      <List dense>
        {contractorCandidates.map((x) => (
          <ListItem
            key={x.link_id}
            secondaryAction={
              x.credential_id || x.entity_id ? (
                <Button
                  href={`/contractor-detail?kind=${x.credential_id ? 'credential' : 'entity'}&id=${encodeURIComponent(x.credential_id ?? x.entity_id ?? '')}`}
                >
                  Contractor
                </Button>
              ) : undefined
            }
          >
            <ListItemText
              primary={x.display_name ?? 'Unnamed contractor record'}
              secondary={`${authorityLabel(x.authority_id) ?? 'Unknown licensing authority'}${x.license_number ? ` · license ${x.license_number}` : ''}${x.candidate_count > 1 ? ` · possible match among ${x.candidate_count} credential records with the same name` : ' · name matches the applicant'}${x.valid_on_activity_date ? ' · credential active during the case' : ' · credential dates unavailable'}`}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption">
        Source {c.source_id} · case ID {c.case_id} · mapper {c.mapper_version ?? '—'} · fetched{' '}
        {c.fetched_at ?? '—'} · raw {c.raw_uri ?? '—'}
      </Typography>
    </Box>
  );
}

function PlanningDetailPage() {
  const routeSearch = Route.useSearch();
  const [input, setInput] = useState(routeSearch.q ?? '');
  const [query, setQuery] = useState(routeSearch.q ?? '');
  const [state, setState] = useState(routeSearch.state ?? '');
  const [family, setFamily] = useState('');
  const [caseId, setCaseId] = useState(routeSearch.caseId ?? '');
  const search = useQuery({
    queryKey: ['planning-search', query, state, family],
    queryFn: () => api.planningSearch(query, state || undefined, family || undefined),
    enabled: query.length >= 2,
  });
  const detail = useQuery({
    queryKey: ['planning-detail', caseId],
    queryFn: () => api.planningDetail(caseId),
    enabled: Boolean(caseId),
  });
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Planning &amp; zoning detail
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search canonical/source case ID, source case number, address, APN/BBL, jurisdiction, or
        applicant. Possible relationships are labeled with the evidence behind the match; links
        whose dates conflict with the case are hidden.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Try a case with linked permits and contractors:
        </Typography>
        {[
          { term: 'SP-2009-0211C', label: 'Austin Country Club cart barn' },
          { term: 'SPC-2007-0583A', label: 'Jardin de Ninos daycare' },
          { term: 'SPC-2022-0141A', label: 'Republic Square' },
        ].map(({ term, label }) => (
          <Button
            key={term}
            size="small"
            variant="outlined"
            onClick={() => {
              setInput(term);
              setQuery(term);
              setCaseId('');
            }}
          >
            {label}
          </Button>
        ))}
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(input);
            setCaseId('');
          }}
          sx={{ display: 'flex', gap: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            label="Search planning cases"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <TextField
            size="small"
            label="State"
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            label="Case family"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            sx={{ width: 180 }}
          />
          <Button type="submit" variant="contained" disabled={input.trim().length < 2}>
            Search
          </Button>
        </Box>
        {(search.isFetching || detail.isFetching) && (
          <CircularProgress size={24} aria-label="Loading planning data" sx={{ mt: 2 }} />
        )}
        {search.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Planning search failed.
          </Alert>
        )}
        {query && !search.isPending && !search.data?.cases.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No planning cases found.
          </Alert>
        )}
        <List>
          {search.data?.cases.map((c) => (
            <CaseSummary key={c.case_id} row={c} onOpen={() => setCaseId(c.case_id)} />
          ))}
        </List>
        {detail.isError && <Alert severity="error">Planning detail failed.</Alert>}
        {detail.data && <Detail data={detail.data} />}
      </Paper>
    </Box>
  );
}
export const Route = createFileRoute('/planning-detail')({
  validateSearch: z.object({
    caseId: z.string().optional(),
    q: z.string().optional(),
    state: z.string().optional(),
  }),
  component: PlanningDetailPage,
});
